import React from "react";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-dragdata";
import ChartDataLabels from "chartjs-plugin-datalabels";
import PropTypes from "prop-types";
import { DataTypes } from "../ChartComponents/DataTypes";

import {
  Chart as ChartJS,
  CategoryScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import HorizontalClusteredBarChart from "./HorizontalClusteredBarChart";

ChartJS.register(
  CategoryScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

class ClustedVerticalBarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: {
        labels: [],
        datasets: [],
      },
      options: this.buildOptions(this.props),
    };

    this.ref = null;
  }

  componentDidMount() {
    this.buildData();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps !== this.props && !this.imgSrc) {
      this.ref.options = this.buildOptions(nextProps);
      this.buildData(nextProps);
      this.ref.update();
    }

    return true;
  }

  buildOptions = (nextProps) => {
    if (this.props.preview && this.props.businessPlan) {
      if (window.chartsForRender.indexOf(this.buildOptions) === -1) {
        window.chartsForRender.push(this.buildOptions);
      }
    }

    let options = {
      type: "bar",
      barRoundness: 0.3,
      maintainAspectRatio: nextProps.chartOptions.lockedAspect
        ? nextProps.chartOptions.lockedAspect
        : false,
      devicePixelRatio: 1.5,
      layout: {
        padding: {
          top: nextProps.chartOptions.showSubtitle || nextProps.chartOptions.showTitle ? 5 : 45,
        },
      },
      animation: {
        duration: 1,
        onComplete: (animation) => {
          if (this.props.preview && this.props.businessPlan && this.ref) {
            this.imgSrc = this.ref.toBase64Image();
            this.forceUpdate(() => {
              window.amountOfChartsRendered++;
              global.Modeliks.NotifySubsctiptions("onChartRenderFinish");
            });
          }
        },
      },
      scales: {
        x: {
          grid: {
            display: nextProps.chartOptions.xGrid ? nextProps.chartOptions.xGrid : false,
            drawBorder: nextProps.chartOptions.xGrid ? nextProps.chartOptions.xGrid : true,
          },
          ticks: {
            display: nextProps.chartOptions.showHorizontalAxis,
            color: "#889299",
            font: {
              weight: 400,
              style: "normal",
              size: nextProps.chartOptions.axisFontSize,
              lineHeight: "160%",
            },
          },
        },
        y: {
          grid: {
            display: nextProps.chartOptions.yGrid ? nextProps.chartOptions.yGrid : false,
            drawBorder: nextProps.chartOptions.xGrid ? nextProps.chartOptions.xGrid : false,
          },
          ticks: {
            display: nextProps.chartOptions.showVerticalAxis,
            color: "#889299",
            maxTicksLimit: 7,
            minTicksLimit: 4,
            font: {
              weight: 400,
              style: "normal",
              size: nextProps.chartOptions.axisFontSize,
              lineHeight: "160%",
            },
            callback: function (value) {
              if (value >= 1000 && value < 1000000) {
                return value / 1000 + "k" + "";
              } else if (value >= 1000000) {
                return value / 1000000 + "mil";
              } else {
                return value;
              }
            },
          },
        },
      },
      plugins: {
        tooltip: {
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 14,
          },
        },
        title: {
          display: nextProps.chartOptions.showTitle ? nextProps.chartOptions.showTitle : false,
          text:
            nextProps.chartOptions.title &&
            nextProps.chartOptions.titleAlign &&
            nextProps.chartOptions.titleAlign === "start"
              ? nextProps.chartOptions.title
              : nextProps.chartOptions.title
                ? nextProps.chartOptions.title
                : "",
          align: nextProps.chartOptions.titleAlign ? nextProps.chartOptions.titleAlign : "center",
          color: "#000000",

          font: {
            size: nextProps.chartOptions.titleFontSize,
          },
          padding: {
            bottom:
              nextProps.chartOptions.showSubtitle && nextProps.chartOptions.showSubtitle === true
                ? 0
                : 45,
          },
        },
        subtitle: {
          display: nextProps.chartOptions.showSubtitle
            ? nextProps.chartOptions.showSubtitle
            : false,
          text:
            nextProps.chartOptions.subtitle &&
            nextProps.chartOptions.titleAlign &&
            nextProps.chartOptions.titleAlign === "start"
              ? nextProps.chartOptions.subtitle
              : nextProps.chartOptions.subtitle
                ? nextProps.chartOptions.subtitle
                : "",
          align: nextProps.chartOptions.titleAlign ? nextProps.chartOptions.titleAlign : "center",
          padding: {
            bottom: 45,
          },
          font: {
            size: nextProps.chartOptions.subTitleFontSize,
          },
        },
        datalabels: {
          anchor: "end",
          align: "end",
          formatter: function (value, ctx) {
            if (nextProps.chartOptions.showDataLabels) {
              return (
                (nextProps.chartOptions.dataType === "currency"
                  ? DataTypes[nextProps.chartOptions.dataType]
                  : "") +
                value.toLocaleString(undefined, {
                  minimumFractionDigits: nextProps.chartOptions.datalabelsDecimalPoints,
                  maximumFractionDigits: nextProps.chartOptions.datalabelsDecimalPoints,
                }) +
                (nextProps.chartOptions.dataType === "percent"
                  ? DataTypes[nextProps.chartOptions.dataType]
                  : "")
              );
            } else {
              return "";
            }
          },
          color: "#252525",
          font: {
            size: nextProps.chartOptions.dataLabelsFontSize,
            weight: "700",
            lineHeight: "17px",
          },
        },
        legend: {
          display: nextProps.chartOptions.displayLegend
            ? nextProps.chartOptions.displayLegend
            : false,
          position: nextProps.chartOptions.legendPosition
            ? nextProps.chartOptions.legendPosition
            : "bottom",
          align: "center",
          labels: {
            usePointStyle: true,
            fontSize: nextProps.chartOptions.legendFontSize,
          },
        },
      },
    };

    return options;
  };

  buildData = (nextProps = null) => {
    let labels = [];
    let datasets = [];
    let rows = this.props.data;
    let colors = this.props.colors;

    rows[0].forEach((label, index) => {
      if (index !== 0) {
        labels.push(label);
      }
    });

    rows.forEach((row, index) => {
      if (index !== 0) {
        if (index >= colors.length) {
          colors = [...colors, ...colors];
          this.props.changeColors(colors);
        }
        let obj = {
          label: "",
          backgroundColor: colors[index - 1],
          // maxBarThickness: this.props.chartOptions.gapWidth ? this.props.chartOptions.gapWidth : 80,
          categoryPercentage:
            nextProps && nextProps.chartOptions.categoryGapWidth
              ? nextProps.chartOptions.categoryGapWidth / 10
              : this.props.chartOptions.categoryGapWidth
                ? this.props.chartOptions.categoryGapWidth / 10
                : 0.6,
          barPercentage:
            nextProps && nextProps.chartOptions.gapWidth
              ? nextProps.chartOptions.gapWidth / 10
              : this.props.chartOptions.gapWidth
                ? this.props.chartOptions.gapWidth / 10
                : 0.3,
          minBarLength: 2,
          borderRadius: 7,
          data: [],
        };
        row.forEach((data, indexData) => {
          if (indexData === 0) {
            obj.label = data;
          } else {
            let parsedData = parseFloat(data);
            if (parsedData % 1 === 0) {
              obj.data.push(parseInt(data));
            } else {
              obj.data.push(parsedData);
            }
          }
        });
        datasets.push(obj);
      }
    });

    this.state.data.labels = labels;
    this.state.data.datasets = datasets;
  };

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    if (this.imgSrc) {
      return <img src={this.imgSrc} style={{ height: "100%", width: "100%" }} />;
    }
    return (
      <Bar
        type={"bar"}
        data={this.state.data}
        ref={this.setRef}
        style={{
          background: this.props.chartOptions.backgroundColor,
          border: `2px solid ${this.props.chartOptions.borderColor}`,
        }}
        options={this.state.options}
      />
    );
  }
}

ClustedVerticalBarChart.config = {
  data: [
    ["", "label1", "label2", "label3"],
    ["ser1", "1", "2", "3"],
    ["ser2", "1", "2", "3"],
  ],
  colors: ["red", "green", "blue", "yellow"],
  dataGridConfig: {
    title: "blabla",
    subtitle: "blabla",
    canAddRows: true,
    canAddColumns: true,
    hasColumnHeaders: true,
    hasRowHeaders: true,
    rowSeries: false,
  },
  dataLalbelsConfig: {
    hideSeries: false,
    dataLabelsOptions: false,
  },
  legendAndGridlinesConfig: {
    hasGridlines: true,
  },
  gapWidthConfig: {
    haveGapWidth: true,
    haveCategoryGapWidth: true,
  },
  defaultConfig: {
    gapWidth: 5,
    categoryGapWidth: 7,
  },
};

ClustedVerticalBarChart.propTypes = {
  data: PropTypes.array.isRequired,
  colors: PropTypes.array.isRequired,
};

export default ClustedVerticalBarChart;
